import { Order, PaymentMethodsType as PaymentMethods, OrderStatus } from "@/types";
import { useMemo } from "react";
import { getOrderSubtotal, getOrderTotal } from "@/utils/order";
import { useParams } from "react-router-dom";
import { useAppSelector } from "@/hooks";
import { format } from "date-fns";
import { eachDayOfInterval } from "date-fns";
import { es } from "date-fns/locale";

export type OrdersByDay = { id: string; day: string; completedOrders: number; rejectedOrders: number; clients: number }[];
export type PaymentPercentages = Record<PaymentMethods, { label: string; value: number }>;

const useOrderStats = (orders?: Order[], daysToShow?: number) => {
  const { organizationId } = useParams();

  const organization = useAppSelector((state) => state.organizations[organizationId || ""]);

  const activeBranch = useAppSelector((state) => state.ui.activeBranch);

  const showTaxes = useMemo(() => {
    return Boolean(organization?.settings?.includeTax);
  }, [organization?.settings?.includeTax]);

  const filteredOrders = useMemo(() => {
    if (!orders?.length) return [];

    if (!activeBranch) return orders;

    return orders.filter((order) => order.branch?.id === activeBranch);
  }, [orders, activeBranch]);

  const results = useMemo(() => {
    let totalProfits = 0;
    const ordersByDay: Record<string, { completedOrders: number; rejectedOrders: number; clients: string[] }> = {};
    const uniqueCustomers: Record<string, NonNullable<Order['client']> & { deliveryLocation?: Order['deliveryLocation']; totalOrders?: number }> = {};

    const paymentTypes = {
      [PaymentMethods.CASH]: {
        label: "Efectivo",
        value: 0,
      },
      [PaymentMethods.CREDIT_CARD]: {
        label: "Tarjetas",
        value: 0,
      },
      [PaymentMethods.BANK_TRANSFER]: {
        label: "Transferencias",
        value: 0,
      },
    };

    const defaultValues = {
      totalOrders: 0,
      totalClients: 0,
      totalProfits: 0,
      ordersByDay: [] as OrdersByDay,
      paymentPercentages: {} as PaymentPercentages,
      uniqueCustomers: {} as Record<string, NonNullable<Order['client']> & { deliveryLocation?: Order['deliveryLocation']; totalOrders?: number }>,
      completionRate: { completed: 0, rejected: 0 },
    };

    if (!filteredOrders || !filteredOrders.length) {
      return defaultValues;
    }

    filteredOrders.forEach((order: Order) => {
      // Set customers
      if (order?.client?.phone) {
        uniqueCustomers[order.client.phone] = {
          ...order.client,
          deliveryLocation: order?.deliveryLocation,
          totalOrders: (uniqueCustomers[order.client.phone]?.totalOrders || 1) + 1,
        };
      }

      // Set total
      const totalWithTaxes = totalProfits + getOrderTotal(order.items);
      const totalWithoutTaxes = totalProfits + getOrderSubtotal(order.items);
      const orderTotal = showTaxes ? totalWithTaxes : totalWithoutTaxes;

      totalProfits = orderTotal;

      // Group days by date
      const orderDate = format(Number(order?.archivedAt || order?.completedAt), "MM/dd/yyyy");

      const isRejected = order.status === OrderStatus.REJECTED;

      if (!ordersByDay[orderDate]) {
        ordersByDay[orderDate] = {
          completedOrders: isRejected ? 0 : 1,
          rejectedOrders: isRejected ? 1 : 0,
          clients: [String(order.client?.phone)]
        };
      } else {
        if (isRejected) {
          ordersByDay[orderDate].rejectedOrders += 1;
        } else {
          ordersByDay[orderDate].completedOrders += 1;
        }

        if (order?.client?.phone && !ordersByDay[orderDate].clients.includes(order.client?.phone)) {
          ordersByDay[orderDate].clients.push(order.client.phone);
        }
      }

      // Set payment methods
      const method = String(order?.paymentDetails?.method) as PaymentMethods;

      if (paymentTypes[method]?.value >= 0) {
        paymentTypes[method].value += 1;
      }
    });

    const newFinalOrdersByDay = () => {
      if (!ordersByDay || Object.keys(ordersByDay).length === 0) {
        return [];
      }

      // Obtén el rango de fechas de los últimos 7 días
      const allDates = Object.keys(ordersByDay).map((dateStr) => new Date(dateStr).getTime());

      let minDate = new Date(Math.min(...allDates));
      const maxDate = new Date(Math.max(...allDates));

      if (daysToShow) {
        // Enforce the exact number of days ending at maxDate
        // subDays from date-fns isn't imported here, so we can do it with JS Date
        const calculatedMin = new Date(maxDate);
        calculatedMin.setDate(maxDate.getDate() - (daysToShow - 1));
        minDate = calculatedMin > minDate ? calculatedMin : minDate;

        // Actually, if we want exactly `daysToShow` days, we should override minDate exactly to calculatedMin,
        // but if orders don't span that far back, doing Math.min is fine.
        // But if we want exactly 7 columns even if there are less days, we should set `minDate = calculatedMin`.
        // The user specifically complained about 8 days instead of 7. If we just bound it to max daysToShow:
        minDate = calculatedMin;
      }

      const daysInInterval = eachDayOfInterval({ start: minDate, end: maxDate });

      // Mapea sobre el rango de fechas para crear el arreglo final
      return daysInInterval.map((date) => {
        const dateStr = format(date, "MM/dd/yyyy");
        const ordersData = ordersByDay[dateStr];

        const formattedDay = format(date, "E", { locale: es });
        const finalDay = formattedDay.charAt(0).toUpperCase() + formattedDay.slice(1);

        return {
          id: dateStr,
          day: finalDay,
          completedOrders: ordersData ? ordersData.completedOrders : 0,
          rejectedOrders: ordersData ? ordersData.rejectedOrders : 0,
          clients: ordersData ? ordersData.clients.length : 0,
        };
      });
    };

    // set payment types percentages
    const total = Object.values(paymentTypes).reduce((sum, type) => sum + type.value, 0);

    const paymentPercentages: PaymentPercentages = { ...paymentTypes };

    // Iterate through the original object and calculate the percentage for each value
    for (const key in paymentTypes) {
      if (Object.prototype.hasOwnProperty.call(paymentTypes, key)) {
        const originalValue = paymentTypes[key].value;
        const percentage = total > 0 ? (originalValue / total) * 100 : 0;

        paymentPercentages[key] = {
          ...paymentTypes[key],
          value: percentage, // Update the value to the calculated percentage
        };
      }
    }

    let totalCompletedOrders = 0;
    let totalRejectedOrders = 0;

    Object.values(ordersByDay).forEach(day => {
      totalCompletedOrders += day.completedOrders;
      totalRejectedOrders += day.rejectedOrders;
    });

    const completionRate = {
      completed: orders.length > 0 ? (totalCompletedOrders / orders.length) * 100 : 0,
      rejected: orders.length > 0 ? (totalRejectedOrders / orders.length) * 100 : 0,
    };

    return {
      // totalOrders: orders.length,
      totalOrders: filteredOrders.length,
      totalClients: Object.keys(uniqueCustomers).length,
      totalProfits,
      ordersByDay: newFinalOrdersByDay(),
      paymentPercentages,
      uniqueCustomers,
      completionRate,
    };
  }, [orders, showTaxes, daysToShow, activeBranch]);

  return results;
};

export default useOrderStats;
